import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  X,
  Command,
  Plus,
  FileText,
  User,
  Settings,
  Filter,
  Globe,
  Film,
  BookOpen,
  Scroll,
  Map,
} from "lucide-react";
import useComprehensiveSearch, { SearchResult } from "../../hooks/useComprehensiveSearch";
import useAccount from "../../hooks/useAccount";

// Default image for cards without images
const DEFAULT_IMAGE = "/images/riseofrealms/the_rust_garden/2.jpg";

// Extended search result type with action property
type ExtendedSearchResult = SearchResult & {
  action?: () => void;
};

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ExtendedSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [searchScope, setSearchScope] = useState<
    "all" | "cards" | "decks" | "realms" | "media" | "tabletop"
  >("all");
  const [resourceFilter, setResourceFilter] = useState<string[]>([]);

  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const account = useAccount();
  
  // Stable navigation functions to prevent re-renders
  const handleNavigate = useCallback((url: string) => {
    navigate(url);
  }, [navigate]);
  
  // Use comprehensive search hook with current filters
  const searchFilters = useMemo(() => {
    const filters: any = {};
    
    if (searchScope !== "all") {
      filters.types = [searchScope];
    }
    
    if (resourceFilter.length > 0) {
      filters.resources = resourceFilter;
    }
    
    return filters;
  }, [searchScope, resourceFilter]);
  
  const {
    results: searchResults,
    loading: searchLoading,
    error: searchError,
    updateQuery,
    resultsByType
  } = useComprehensiveSearch(query, searchFilters, { 
    debounceMs: 300,
    limit: 20,
    minQueryLength: 1
  });

  // Define common pages for quick navigation (memoized without JSX to prevent re-creation)
  const commonPages = useMemo(
    () => [
      {
        id: "search",
        title: "Advanced Search",
        url: "/rise-of-realms/search",
      },
      {
        id: "deckbuilder", 
        title: "Deck Builder",
        url: "/rise-of-realms/deckbuilder",
      },
      {
        id: "play",
        title: "Play Game",
        url: "/rise-of-realms/play",
      },
      {
        id: "store",
        title: "Card Store",
        url: "/store",
      },
      {
        id: "profile",
        title: "My Profile",
        url: "/login",
      },
    ],
    []
  );

  // List of resource types for filter
  const resourceTypes = ["gold", "wood", "stone", "iron", "food"];

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Close with escape
      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Focus the input when the modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Close the modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Update search query when input changes
  useEffect(() => {
    updateQuery(query);
  }, [query]); // Remove updateQuery from dependencies to prevent infinite loop

  // Process search results and add navigation actions
  useEffect(() => {
    if (!query.trim()) {
      // Show default suggestions/recent searches when query is empty
      const defaultResults: ExtendedSearchResult[] = [
        ...commonPages.map((page) => ({
          id: page.id,
          type: "page" as const,
          title: page.title,
          url: page.url,
          action: () => handleNavigate(page.url),
          score: 1,
          tags: []
        })),
      ];

      setResults(defaultResults);
      return;
    }

    // Convert search results to include navigation actions
    const processedResults = searchResults.map((result) => ({
      ...result,
      action: () => handleNavigate(result.url),
    }));

    // Add admin actions if applicable
    let finalResults = [...processedResults];
    
    if (account?.isAdmin && query.toLowerCase().includes("create")) {
      finalResults.push({
        id: "create-card",
        type: "setting" as const,
        title: "Create New Card",
        subtitle: "Open card creator with current query",
        url: `/create-card?name=${encodeURIComponent(query)}`,
        action: () => handleNavigate(`/create-card?name=${encodeURIComponent(query)}`),
        score: 1,
        tags: []
      });
    }

    // Add action for advanced search if we have results
    if (finalResults.length > 0) {
      finalResults.push({
        id: "advanced-search",
        type: "setting" as const,
        title: "View in Advanced Search",
        subtitle: "See all results with advanced filtering",
        url: `/rise-of-realms/search?q=${encodeURIComponent(query)}`,
        action: () =>
          handleNavigate(`/rise-of-realms/search?q=${encodeURIComponent(query)}`),
        score: 1,
        tags: []
      });
    }

    setResults(finalResults);
    setSelectedIndex(0); // Reset selection when results change
  }, [
    query,
    searchResults,
    commonPages,
    account?.isAdmin, // Only depend on the specific property we use
    handleNavigate,
  ]);

  // Handle keyboard navigation in results
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      handleResultClick(results[selectedIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  // Handle clicking on a result
  const handleResultClick = (result: ExtendedSearchResult) => {
    if (result.action) {
      result.action();
    } else {
      handleNavigate(result.url);
    }
    onClose();
  };

  // Toggle resource filter
  const toggleResourceFilter = (resource: string) => {
    setResourceFilter((prev) =>
      prev.includes(resource)
        ? prev.filter((r) => r !== resource)
        : [...prev, resource]
    );
  };

  // Reset query when closed
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // We're handling visibility in context.tsx with conditional rendering
  // so we don't need this check anymore
  // if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black bg-opacity-50">
      <div
        ref={searchRef}
        className="w-full max-w-2xl bg-gray-900 rounded-lg shadow-2xl border border-gray-700 overflow-hidden"
      >
        {/* Search Input */}
        <div className="relative">
          <div className="relative flex items-center p-4 border-b border-gray-700">
            <Search className="w-5 h-5 text-gray-400 mr-3" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search cards, decks, realms, media, tabletop..."
              className="flex-1 bg-transparent outline-none text-white placeholder-gray-400"
              autoComplete="off"
            />
            <div className="flex items-center gap-2">
              <button
                className="p-1 hover:bg-gray-700 rounded-md"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-5 h-5 text-gray-400" />
              </button>
              <div className="px-2 py-1 bg-gray-800 rounded text-xs text-gray-400 flex items-center gap-1">
                <Command className="w-3 h-3" />
                <span>K</span>
              </div>
            </div>
          </div>

          {/* Search Filters */}
          {showFilters && (
            <div className="p-4 border-b border-gray-700 bg-gray-800">
              <div className="flex flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-300">Search:</span>
                  <select
                    value={searchScope}
                    onChange={(e) => setSearchScope(e.target.value as any)}
                    className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white"
                  >
                    <option value="all">All</option>
                    <option value="cards">Cards</option>
                    <option value="decks">Decks</option>
                    <option value="realms">Realms</option>
                    <option value="media">Media</option>
                    <option value="tabletop">Tabletop</option>
                  </select>
                </div>

              </div>

              <div>
                <span className="text-sm text-gray-300 mb-2 block">
                  Resources:
                </span>
                <div className="flex flex-wrap gap-2">
                  {resourceTypes.map((resource) => (
                    <button
                      key={resource}
                      className={`px-2 py-1 text-xs rounded capitalize ${
                        resourceFilter.includes(resource)
                          ? "bg-blue-600 text-white"
                          : "bg-gray-700 text-gray-300"
                      }`}
                      onClick={() => toggleResourceFilter(resource)}
                    >
                      {resource}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          )}
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {searchLoading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500 mb-2"></div>
              <p>Searching...</p>
            </div>
          ) : searchError ? (
            <div className="p-8 text-center text-red-400">
              <p>Search failed</p>
              <p className="text-sm mt-2">{searchError}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No results found</p>
              <p className="text-sm mt-2">Try different keywords or filters</p>
            </div>
          ) : (
            <div className="p-1">
              {results.map((result, index) => (
                <div
                  key={`${result.type}-${result.id}`}
                  className={`px-4 py-3 rounded-md cursor-pointer ${
                    index === selectedIndex
                      ? "bg-gray-700"
                      : "hover:bg-gray-800"
                  }`}
                  onClick={() => handleResultClick(result)}
                >
                  <div className="flex items-center">
                    {/* Thumbnail/Icon */}
                    <div className="flex-shrink-0 mr-3">
                      {result.type === "card" && result.imageUrl ? (
                        <div className="w-10 h-14 rounded overflow-hidden border border-gray-700">
                          <img
                            src={result.imageUrl}
                            alt={result.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : result.type === "deck" && result.imageUrl ? (
                        <div className="w-10 h-10 rounded overflow-hidden border border-gray-700">
                          <img
                            src={result.imageUrl}
                            alt={result.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-md">
                          {result.type === "page" && (
                            <Command size={20} className="text-gray-400" />
                          )}
                          {result.type === "player" && (
                            <User size={20} className="text-gray-400" />
                          )}
                          {result.type === "setting" && (
                            <Settings size={20} className="text-gray-400" />
                          )}
                          {result.type === "realm" && (
                            <Globe size={20} className="text-purple-400" />
                          )}
                          {result.type === "media" && (
                            <Film size={20} className="text-blue-400" />
                          )}
                          {result.type === "tabletop" && (
                            <BookOpen size={20} className="text-orange-400" />
                          )}
                          {result.type === "rule" && (
                            <Scroll size={20} className="text-yellow-400" />
                          )}
                          {(result.type === "card" ||
                            result.type === "deck") && (
                            <FileText size={20} className="text-gray-400" />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Text Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">
                        {result.title}
                      </h4>
                      {result.subtitle && (
                        <p className="text-sm text-gray-400 truncate">
                          {result.subtitle}
                        </p>
                      )}
                    </div>

                    {/* Result Type */}
                    <div className="ml-3">
                      <span className="text-xs px-2 py-1 bg-gray-800 rounded-full text-gray-400 capitalize">
                        {result.type}
                      </span>
                    </div>
                  </div>

                  {/* Additional Metadata */}
                  {result.type === "card" && result.metadata && (
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                      {result.metadata.produces &&
                        result.metadata.produces.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-green-800 text-green-200 rounded">
                            Produces: {result.metadata.produces.join(", ")}
                          </span>
                        )}
                      {result.metadata.requires &&
                        result.metadata.requires.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-red-800 text-red-200 rounded">
                            Requires: {result.metadata.requires.join(", ")}
                          </span>
                        )}
                      {result.metadata.unlocks &&
                        result.metadata.unlocks.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-blue-800 text-blue-200 rounded">
                            Unlocks: {result.metadata.unlocks.join(", ")}
                          </span>
                        )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-700 text-xs text-gray-400">
          <div className="flex justify-between items-center">
            <div>
              <span>{results.length} results</span>
            </div>
            <div className="flex gap-3">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center border border-gray-500 rounded-sm text-xs">
                  ↑
                </div>
                <div className="w-4 h-4 flex items-center justify-center border border-gray-500 rounded-sm text-xs">
                  ↓
                </div>
                <span>Navigate</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center border border-gray-500 rounded-sm text-xs">
                  ↵
                </div>
                <span>Select</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center border border-gray-500 rounded-sm text-xs">
                  ESC
                </div>
                <span>Close</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
